import React, { useState, useEffect, createContext } from "react";
import NProgress from "nprogress";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import AppLayout from "./components/Layouts/AppLayout.js";
import Home from "./components/Home.js";
import CustomerList from "./components/CustomerList";
import ContactList from "./components/ContactList.js";
import Overview from "./components/TimeSheet/Overview.js";
import SignIn from "./components/SignIn/SignIn.js";
import ForgotPassword from "./components/SignIn/ForgotPassword.js";
import ResetPassword from "./components/SignIn/ResetPassword.js";
import ProjectList from "./components/Projects/ProjectList";
import ProjectLayout from "./components/Layouts/ProjectLayout";
import ProjectUsers from "./components/ProfileUser/ProjectUsers";
import { AuthProvider, useAuth } from "./components/Authentication/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import TimeSheetTable from "./components/TimeSheet/TimesheetTable";
import ProjectDetails from "./components/Projects/ProjectDetails";
import PhaseDetails from "./components/Projects/PhaseDetails.js";
import VariationDetails from "./components/TimeSheet/VariationDetails";
import IssueDetails from "./components/TimeSheet/IssueDetails";
import General from "./components/TimeSheet/General";
import GroupList from "./components/GroupList.js";
import GroupDetails from "./components/GroupDetails.js";
import Settings from "./components/Settings/Settings.js";
import DashBoard from "./components/Projects/Dashboard.js";
import UserDetails from "./components/UserDetails/UserDetails.js";
import Rfi from "./components/Projects/RfiList.js";
import RfiForClient from "./components/RFI/RfiForClient.js";
// import IndividualEstimation from "./components/Projects/individualEstimation.js";
import { Estimation } from "./components/Projects/Estimation";
import {
  RoleProvider,
  useRolePermissions,
} from "./components/Settings/RoleContext";
import ErrorBoundary from "./components/Error Boundary/ErrorBoundary .js";
import EstimatesPhase from "./components/Projects/EstimatesPhase.js";
import EstimateRevisionTable from "./components/Projects/EstimateRevisionTable.js";
import EstimateTasks from "./components/Projects/EstimatesTasks.js";
import Report from "./components/Report/Report.js";
import ToDoList from "./components/Projects/TodoList.js";
import api from "../../frontend/src/api/axiosConfig.js";
import "nprogress/nprogress.css";
import VariationForClient from "./components/Variation/VariationForClient.js";

export const ProjectContext = createContext();

const RouteChangeHandler = () => {
  const location = useLocation();

  useEffect(() => {
    // Start progress bar on route change
    NProgress.start();
    // Complete progress bar once route change is done
    const timeout = setTimeout(() => NProgress.done(), 1000);

    return () => clearTimeout(timeout);
  }, [location]);

  return null;
};

const AppContent = () => {
  const [refresh, setRefresh] = useState(false);
  const [username, setUsername] = useState("");
  const [dialBoxopen, setDialBoxopen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isNavBarOpen, setIsNavBarOpen] = useState(() => {
    const stored = localStorage.getItem("isNavBarOpen");
    return stored !== null ? JSON.parse(stored) : true;
  });
  useEffect(() => {
    localStorage.setItem("isNavBarOpen", JSON.stringify(isNavBarOpen));
  }, [isNavBarOpen]);
  const [addTimesheetOpen, setAddTimesheetOpen] = useState(false);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const { hasPermission } = useRolePermissions();
  const hasEstimateAccess = hasPermission("estimates", "read");
  NProgress.configure({
    showSpinner: false,
  });
  const [loading, setLoading] = useState(false);

  const onFrameButtonClick = () => {
    setDialBoxopen((prev) => !prev);

    document.body.classList.remove("no-scroll");
  };

  const { isAuthenticated } = useAuth();

  // Helper function to create protected routes
  const createProtectedRoute = (path, element, requiredRoles = []) => (
    <Route
      path={path}
      element={
        <ProtectedRoute requiredRoles={requiredRoles}>{element}</ProtectedRoute>
      }
    />
  );

  return (
    <ProjectContext.Provider
      value={{
        dialBoxopen,
        setDialBoxopen,
        onFrameButtonClick,
        openDialog,
        setOpenDialog,
        setIsNavBarOpen,
        isNavBarOpen,
        setAddTimesheetOpen,
        addTimesheetOpen,
        setAddProjectOpen,
        addProjectOpen,
      }}
    >
      <div>
        {isAuthenticated && <AppLayout username={username} />}
        <div className="content-container">
          {loading ? (
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <span className="loading-animation">LOADING...</span>
            </div>
          ) : (
            <Routes>
              <Route path="/" element={<SignIn setUsername={setUsername} />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route
                path="/variation/approval/:variationId/:token"
                element={<VariationForClient />}
              />
              <Route
                path="/:projectId/:rfiId/:token"
                element={<RfiForClient />}
              />
              {createProtectedRoute("/project/:projectId", <ProjectLayout />)}
              {createProtectedRoute("/home", <Home />)}
              {hasPermission("reports", "read") &&
                createProtectedRoute("/report", <Report />)}
              {createProtectedRoute(
                "/project",
                <ProjectList refresh={refresh} />
              )}
              {createProtectedRoute("/todoList", <ToDoList />)}
              {createProtectedRoute(
                "/customerlist",
                <CustomerList refresh={refresh} />,
                ["Administrator", "Super Admin"]
              )}
              {createProtectedRoute(
                "/api/customers/:customerId/contacts",
                <ContactList refresh={refresh} />,
                ["Administrator", "Super Admin"]
              )}
              {createProtectedRoute(
                "/grouplist",
                <GroupList refresh={refresh} />,
                ["Administrator", "Super Admin"]
              )}
              {createProtectedRoute(
                "/group/:groupId",
                <GroupDetails refresh={refresh} />,
                ["Administrator", "Super Admin"]
              )}
              {hasPermission("settings", "read") &&
                createProtectedRoute("/settings", <Settings />)}
              {createProtectedRoute("/timesheet/overview", <Overview />)}
              {createProtectedRoute(
                "/project/:projectId/users",
                <div>
                  <ProjectLayout />
                  <ProjectUsers />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/timesheets/general",
                <div>
                  <ProjectLayout />
                  <General />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/dashboard",
                <div>
                  <ProjectLayout />
                  <DashBoard />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/timesheets",
                <div>
                  <ProjectLayout />
                  <TimeSheetTable refresh={refresh} />
                </div>
              )}
              {createProtectedRoute(
                "/project/phases/:projectId",
                <div>
                  <ProjectLayout />
                  <ProjectDetails />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/phase/:phaseId",
                <div>
                  <ProjectLayout />
                  <PhaseDetails />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/timesheets/variation",
                <div>
                  <ProjectLayout />
                  <VariationDetails />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/rfi",
                <div>
                  <ProjectLayout />
                  <Rfi />
                </div>
              )}
              {createProtectedRoute(
                "/project/:projectId/timesheets/issue",
                <div>
                  <ProjectLayout />
                  <IssueDetails />
                </div>
              )}
              {createProtectedRoute(
                "/settings/user-details",
                <div>
                  <UserDetails />
                </div>
              )}
              {createProtectedRoute(
                "/user-details/:id",
                <div>
                  <UserDetails />
                </div>
              )}
              {hasEstimateAccess && (
                <>
                  {createProtectedRoute("/estimate", <Estimation />)}
                  {/* {createProtectedRoute(
                    "/estimate/:estimateId",
                    <div>
                      <IndividualEstimation />
                    </div>
                  )} */}
                  {createProtectedRoute(
                    "/estimate/phases/:estimateId",
                    <div>
                      <EstimatesPhase />
                    </div>
                  )}
                  {createProtectedRoute(
                    "/estimate/revision/:templateName/:estimateId/:phaseId",
                    <div>
                      <EstimateRevisionTable />
                    </div>
                  )}
                  {createProtectedRoute(
                    "/estimate/phases/:estimateId/:phaseId",
                    <div>
                      <EstimateTasks />
                    </div>
                  )}
                </>
              )}
            </Routes>
          )}
        </div>
      </div>
    </ProjectContext.Provider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <RoleProvider>
        <Router>
          <RouteChangeHandler />
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </Router>
      </RoleProvider>
    </AuthProvider>
  );
};

export default App;
